<template>
    <ValidationObserver ref="observer">
        <div class="panel sticky">
            <div class="panel-title">Đánh giá chất lượng tác vụ chấm bài của giảng viên</div>

            <div v-for="g in groups" :key="g.key" class="criteria-card">
                <div :class="['criteria-title', { primary: g.primary }]"># {{ g.title }}</div>

                <!-- Nhận xét đúng -->
                <div class="line">
                    <span class="chip chip-danger">Nhận xét đúng</span>
                </div>
                <div class="control-row">
                    <span class="control-label">Đánh giá tiêu chí: <span class="req">*</span></span>
                    <ValidationProvider :name="`${g.title} - Nhận xét đúng`" rules="required" v-slot="{ errors }">
                        <el-select :value="form[g.key].right.value" placeholder="Chọn điểm" class="w-160"
                            :disabled="isShowMode" @input="v => onPick(v, form[g.key].right)">
                            <el-option label="Đạt" :value="1" />
                            <el-option label="Không đạt" :value="2" />
                        </el-select>
                        <input type="hidden" :value="form[g.key].right.value" />
                        <div class="validation-error" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                </div>
                <div v-if="form[g.key].right.value === 2" class="note-wrap">
                    <label class="note-label">Nhận xét chi tiết <span class="req">*</span></label>
                    <ValidationProvider :name="`${g.title} - Nhận xét chi tiết (Nhận xét đúng)`" rules="required"
                        v-slot="{ errors }">
                        <el-input type="textarea" :rows="3" :value="form[g.key].right.note"
                            :readonly="isShowMode" @input="v => onNote(v, form[g.key].right)" placeholder="Nhập nhận xét" />
                        <div class="validation-error" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                </div>

                <!-- Sửa lỗi đúng -->
                <div class="line mt-6">
                    <span class="chip chip-danger"> Sửa lỗi đúng</span>
                </div>
                <div class="control-row">
                    <span class="control-label">Đánh giá tiêu chí: <span class="req">*</span></span>
                    <ValidationProvider :name="`${g.title} - Sửa lỗi đúng`" rules="required" v-slot="{ errors }">
                        <el-select :value="form[g.key].fix.value" placeholder="Chọn điểm" class="w-160"
                            :disabled="isShowMode" @input="v => onPick(v, form[g.key].fix)">
                            <el-option label="Đạt" :value="1" />
                            <el-option label="Không đạt" :value="2" />
                        </el-select>
                        <input type="hidden" :value="form[g.key].fix.value" />
                        <div class="validation-error" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                </div>
                <div v-if="form[g.key].fix.value === 2" class="note-wrap">
                    <label class="note-label">Nhận xét chi tiết <span class="req">*</span></label>
                    <ValidationProvider :name="`${g.title} - Nhận xét chi tiết (Sửa lỗi đúng)`" rules="required"
                        v-slot="{ errors }">
                        <el-input type="textarea" :rows="3" :value="form[g.key].fix.note"
                            :readonly="isShowMode" @input="v => onNote(v, form[g.key].fix)" placeholder="Nhập nhận xét" />
                        <div class="validation-error" v-if="errors[0]">{{ errors[0] }}</div>
                    </ValidationProvider>
                </div>

                <el-divider class="soft-divider" />
            </div>

            <!-- Edit mode buttons -->
            <div v-if="!isShowMode" class="text-right">
                <button v-if="activeTab === 'main' && (isMainFormDirty || (!isMainFormDirty && this.supplementDetailLength == 0 ))" class="btn btn-primary font-weight-bold" :disabled="saving || !canSubmit" @click="handleSave">
                    <i class="fas fa-save"></i> {{ saving ? 'Đang lưu...' : 'Lưu' }}
                </button>
                <button v-if="activeTab === 'supplement'" class="btn btn-secondary font-weight-bold mr-2" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Quay lại
                </button>
                <button v-if="!isMainFormDirty && this.supplementDetailLength > 0" class="btn btn-primary font-weight-bold"
                    @click="handleSave" :disabled="!canSubmit">
                    <i class="fas fa-arrow-right"></i> Tiếp theo
                </button>
            </div>

            <!-- Show mode navigation buttons -->
            <div v-if="isShowMode" class="text-right">
                <button v-if="showBackButton" class="btn btn-secondary font-weight-bold mr-2" @click="goBack">
                    <i class="fas fa-arrow-left"></i> Quay lại
                </button>
                <button v-if="showNextButton" class="btn btn-primary font-weight-bold" @click="goNext">
                    <i class="fas fa-arrow-right"></i> Tiếp theo
                </button>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

export default {
    name: 'InspectionEvaluate',
    components: { ValidationObserver, ValidationProvider },
    props: {
        form: { type: Object, required: true },
        saving: { type: Boolean, default: false },
        activeTab: { type: String, default: 'main' },
        isShowMode: { type: Boolean, default: false },
        currentSupplementIndex: { type: Number, default: 0 },
        supplementDetailLength: { type: Number, default: 0 },
        isSupplementFormDirty: { type: Boolean, default: false },
        isMainFormDirty: { type: Boolean, default: false }
    },
    model: { prop: 'form', event: 'update:form' },
    data() {
        return {
            groups: [
                { key: 'ta', title: 'Task Achievement', primary: true },
                { key: 'cc', title: 'Coherence and Cohesion', primary: true },
                { key: 'lr', title: 'Lexical Resource', primary: true },
                { key: 'gra', title: 'Grammatical Range and Accuracy', primary: true }
            ]
        };
    },
    created() {
        // Register validation rules
        extend('required', {
            ...required,
            message: 'Trường này không được bỏ trống'
        });
    },
    computed: {
        canSubmit() {
            // Check if all criteria are evaluated
            return this.groups.every(g => {
                const groupData = this.form[g.key];
                if (!groupData) return false;

                // Check if both right and fix have values
                const rightValid = groupData.right.value !== null && groupData.right.value !== undefined;
                const fixValid = groupData.fix.value !== null && groupData.fix.value !== undefined;

                // If value is 2 (Không đạt), check if note is provided
                const rightNoteValid = groupData.right.value !== 2 || (groupData.right.note && groupData.right.note.trim() !== '');
                const fixNoteValid = groupData.fix.value !== 2 || (groupData.fix.note && groupData.fix.note.trim() !== '');

                return rightValid && fixValid && rightNoteValid && fixNoteValid;
            });
        },
        showBackButton() {
            if (!this.isShowMode || this.activeTab == 'main') return false;
            return this.activeTab == 'supplement';
        },
        showNextButton() {
            if (!this.isShowMode || this.supplementDetailLength == 0) return false;
            // If we're on supplement tab and this is the last supplement item, don't show next button
            if (this.activeTab === 'supplement' && this.currentSupplementIndex >= this.supplementDetailLength - 1) {
                return false;
            }

            return true;
        }
    },
    methods: {
        onPick(value, node) {
            // 1 = Đạt, 2 = Không đạt
            node.value = value;
            if (value === 2 && !node.note) node.note = '';
            this.emitSync();
        },
        onNote(value, node) {
            node.note = value;
            this.emitSync(false);
        },
        resetValidation() {
            // Clear all validation states and errors
            if (this.$refs.observer && this.$refs.observer.reset) {
                this.$refs.observer.reset();
            }
        },
        async handleSave() {
            // Validate form before emitting save
            const isValid = await this.$refs.observer.validate();

            if (isValid && this.canSubmit) {
                if((!this.isMainFormDirty && this.activeTab === 'main' && this.supplementDetailLength > 0) || (!this.isSupplementFormDirty && this.activeTab === 'supplement')) {
                    this.$emit('goNext');
                } else {
                    this.$emit('save');
                }
            }
        },
        emitSync(clone = true) {
            const payload = clone ? JSON.parse(JSON.stringify(this.form)) : this.form;
            this.$emit('update:form', payload);
        },
        goBack() {
            this.$emit('goBack');
        },
        goNext() {
            this.$emit('goNext');
        }
    }
};
</script>

<style scoped>
.panel {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    padding: 12px
}

.sticky {
    position: sticky;
    top: 12px;
}

.panel-title {
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 12px;
    color: #4F4F4F;
}

.criteria-card {
    padding: 10px 0
}

.criteria-title {
    font-weight: 700;
    color: #111827;
    margin-bottom: 10px
}

.criteria-title.primary {
    color: #6f42c1
}


.line {
    display: block
}

.control-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
}

.control-label {
    font-size: 13px;
    color: #6b7280;
    white-space: nowrap;
}

.w-160 {
    width: 160px
}

.note-wrap {
    margin-top: 8px
}

.note-label {
    display: block;
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 6px
}

.req {
    color: #ef4444;
    margin-right: 4px
}

.chip {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 8px;
    font-size: 12px;
    line-height: 1;
    border: 1px solid transparent;
}

.chip-danger {
    background: #fee4e2;
    border-color: #fecdca;
    color: #b42318
}

.soft-divider {
    margin: 14px 0
}

.soft-divider>>>.el-divider__text {
    color: #9aa1ae
}

.validation-error {
    color: #ef4444;
    font-size: 12px;
    margin-top: 4px;
}
</style>